import { useRef, useState } from 'react';

import { Button, Icon } from '@ds/core';
import { ImportMapper } from '@ds/data';
import type { CampoDeImportacao } from '@ds/data';
import { Alert, Modal } from '@ds/feedback';
import { lerArquivoDeTexto, lerCsv } from '@/comuns/csv';
import type { Planilha } from '@/comuns/csv';
import { exportarComo } from '@/comuns/exportar';
import { useAviso } from '@/comuns/toast';
import { LIMITE_DE_LINHAS, montarImportacao } from '@/data/importacao';
import { dados } from '@/data/porta';
import type { RecusaDaImportacao, ResultadoDaImportacao, TipoImportavel } from '@/data/tipos';

/* Campos de destino por tipo: importar lead e importar paciente não pedem a
   mesma coisa, e oferecer campo que o tipo não usa gera dado órfão. */
const CAMPOS: Record<TipoImportavel, CampoDeImportacao[]> = {
    paciente: [
        { chave: 'nome', rotulo: 'Nome completo', obrigatorio: true, sinonimos: ['paciente', 'nomecompleto', 'cliente'] },
        { chave: 'tel', rotulo: 'Telefone', exemplo: '(11) 99999-9999', obrigatorio: true, sinonimos: ['celular', 'fone', 'whatsapp', 'telefone1'] },
        { chave: 'email', rotulo: 'E-mail', exemplo: 'nome@email.com', sinonimos: ['mail'] },
        { chave: 'cpf', rotulo: 'CPF', exemplo: '000.000.000-00', sinonimos: ['documento'] },
        { chave: 'nasc', rotulo: 'Data de nascimento', exemplo: 'dd/mm/aaaa', sinonimos: ['nascimento', 'datanasc', 'aniversario'] },
        { chave: 'sexo', rotulo: 'Sexo', sinonimos: ['genero'] },
        { chave: 'cep', rotulo: 'CEP', sinonimos: ['codigopostal'] },
        { chave: 'endereco', rotulo: 'Endereço', sinonimos: ['rua', 'logradouro'] },
        { chave: 'cidade', rotulo: 'Cidade', sinonimos: ['municipio'] },
        { chave: 'obs', rotulo: 'Observações', sinonimos: ['anotacoes', 'observacao'] },
    ],
    lead: [
        { chave: 'nome', rotulo: 'Nome', obrigatorio: true, sinonimos: ['lead', 'contato'] },
        { chave: 'tel', rotulo: 'Telefone', exemplo: '(11) 99999-9999', obrigatorio: true, sinonimos: ['celular', 'fone', 'whatsapp'] },
        { chave: 'email', rotulo: 'E-mail', sinonimos: ['mail'] },
        { chave: 'origem', rotulo: 'Origem', exemplo: 'Instagram, Indicação…', sinonimos: ['canal', 'fonte'] },
        { chave: 'interesse', rotulo: 'Interesse', sinonimos: ['procedimento', 'servico'] },
        { chave: 'responsavel', rotulo: 'Responsável', sinonimos: ['vendedor', 'consultor'] },
        { chave: 'valor', rotulo: 'Valor estimado', exemplo: 'R$ 0,00', sinonimos: ['ticket', 'orcamento'] },
    ],
    profissional: [
        { chave: 'nome', rotulo: 'Nome', obrigatorio: true, sinonimos: ['profissional'] },
        { chave: 'email', rotulo: 'E-mail', obrigatorio: true, sinonimos: ['mail'], exemplo: 'com ele a pessoa cria a conta' },
        { chave: 'tel', rotulo: 'Telefone', sinonimos: ['celular', 'fone'] },
        { chave: 'papel', rotulo: 'Função', exemplo: 'Dermatologista', sinonimos: ['cargo', 'especialidade'] },
        { chave: 'registro', rotulo: 'Registro no conselho', exemplo: 'CRM-SP 000.000', sinonimos: ['crm', 'conselho', 'crbm'] },
    ],
};

/* Uma linha de exemplo por tipo, para o modelo baixado já mostrar o formato. */
const EXEMPLO: Record<TipoImportavel, string[]> = {
    paciente: ['Maria Exemplo', '(11) 99999-0000', 'maria@example.com', '', '15/04/1990', 'F', '01000-000', 'Rua Exemplo, 100', 'São Paulo', ''],
    lead: ['João Exemplo', '(11) 98888-0000', 'joao@example.com', 'Instagram', 'Limpeza de pele', '', 'R$ 380,00'],
    profissional: ['Dra. Exemplo', 'dra.exemplo@example.com', '(11) 97777-0000', 'Dermatologista', 'CRM-SP 000.000'],
};

const DEZ_MB = 10 * 1024 * 1024;

export interface ImportarContatosModalProps {
    open: boolean;
    tipo: TipoImportavel;
    onClose: () => void;
    onImportado: () => void;
}

/**
 * Importação em três passos: escolher o CSV, ligar as colunas, conferir o que
 * entrou e o que não entrou — com o número da linha e o motivo.
 *
 * Carregava sempre uma planilha de exemplo e dizia "Importação iniciada" sem
 * gravar nada.
 */
export function ImportarContatosModal({ open, tipo, onClose, onImportado }: ImportarContatosModalProps) {
    const aviso = useAviso();
    const entrada = useRef<HTMLInputElement | null>(null);
    const [arquivo, setArquivo] = useState<{ nome: string; planilha: Planilha } | null>(null);
    const [sobreArea, setSobreArea] = useState(false);
    const [lendo, setLendo] = useState(false);
    const [erro, setErro] = useState<string | null>(null);
    const [importando, setImportando] = useState(false);
    const [resultado, setResultado] = useState<ResultadoDaImportacao | null>(null);

    const fechar = () => {
        setArquivo(null);
        setErro(null);
        setResultado(null);
        onClose();
    };

    const receber = async (escolhido: File | undefined) => {
        if (!escolhido) return;
        setErro(null);
        if (/\.xlsx?$/i.test(escolhido.name)) {
            setErro('Planilha do Excel: salve como CSV (Arquivo › Salvar como › CSV) e envie de novo.');
            return;
        }
        if (!/\.csv$/i.test(escolhido.name) && escolhido.type !== 'text/csv') {
            setErro(`${escolhido.name} não é um CSV.`);
            return;
        }
        if (escolhido.size > DEZ_MB) {
            setErro('O arquivo passa de 10 MB. Divida a planilha em partes.');
            return;
        }
        setLendo(true);
        try {
            const planilha = lerCsv(await lerArquivoDeTexto(escolhido));
            if (planilha.colunas.length === 0 || planilha.linhas.length === 0) {
                setErro('A planilha está vazia. A primeira linha precisa ser o cabeçalho, e as seguintes, as pessoas.');
                return;
            }
            setArquivo({ nome: escolhido.name, planilha });
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível ler o arquivo.');
        } finally {
            setLendo(false);
        }
    };

    const importar = async (mapa: Record<string, string>) => {
        if (!arquivo) return;
        const { validas, recusadas } = montarImportacao(tipo, arquivo.planilha, mapa);
        setImportando(true);
        try {
            const gravado = validas.length > 0
                ? await dados().importarContatos(tipo, validas)
                : { criados: 0, recusadas: [], avisos: [] };
            const todas: RecusaDaImportacao[] = [...recusadas, ...gravado.recusadas].sort((a, b) => a.linha - b.linha);
            setResultado({ criados: gravado.criados, recusadas: todas, avisos: gravado.avisos });
            if (gravado.criados > 0) onImportado();
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'A importação falhou.');
        } finally {
            setImportando(false);
        }
    };

    const baixarModelo = () => {
        const campos = CAMPOS[tipo];
        const mensagem = exportarComo('csv', {
            title: `Modelo de importação — ${tipo === 'paciente' ? 'pacientes' : tipo === 'lead' ? 'leads' : 'profissionais'}`,
            columns: campos.map((c, i) => ({ header: c.rotulo, value: (linha: string[]) => linha[i] ?? '' })),
            rows: [EXEMPLO[tipo]],
        });
        aviso({ title: mensagem, description: 'Preencha a partir da segunda linha e envie aqui.', tone: 'info' });
    };

    const descricao = resultado
        ? 'Confira o que entrou. Linha que não entrou volta com o motivo, para corrigir na planilha.'
        : arquivo
            ? 'Ligue cada coluna da planilha ao campo da Fórmula MED. O que reconhecemos já vem preenchido.'
            : `Envie um arquivo CSV com até ${LIMITE_DE_LINHAS} linhas. No passo seguinte você escolhe o que vai em cada campo.`;

    return (
        <Modal
            open={open} onClose={fechar} width={860} title="Importar contatos" description={descricao}
            /* Planilha lida e colunas ligadas é trabalho: Esc não pode jogar fora. */
            dirty={arquivo !== null && resultado === null}
            footer={resultado ? <Button onClick={fechar}>Fechar</Button> : arquivo ? undefined : <Button variant="secondary" onClick={fechar}>Cancelar</Button>}
        >
            {erro && <div style={{ marginBottom: 14 }}><Alert tone="danger" dismissible={false}>{erro}</Alert></div>}

            {resultado ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <Alert
                        tone={resultado.criados > 0 ? 'success' : 'warning'} dismissible={false}
                        title={`${resultado.criados} ${resultado.criados === 1 ? 'contato importado' : 'contatos importados'}`}
                    >
                        {resultado.recusadas.length > 0
                            ? `${resultado.recusadas.length} ${resultado.recusadas.length === 1 ? 'linha não entrou' : 'linhas não entraram'}.`
                            : 'Todas as linhas entraram.'}
                    </Alert>
                    {[...resultado.recusadas.map((r) => ({ ...r, tipo: 'recusa' as const })), ...resultado.avisos.map((r) => ({ ...r, tipo: 'aviso' as const }))]
                        .sort((a, b) => a.linha - b.linha)
                        .slice(0, 50)
                        .map((r) => (
                            <p key={`${r.tipo}-${r.linha}-${r.motivo}`} style={{ margin: 0, fontSize: 13, color: r.tipo === 'recusa' ? 'var(--danger-strong)' : 'var(--warning-strong)' }}>
                                Linha {r.linha}: {r.motivo}
                            </p>
                        ))}
                </div>
            ) : !arquivo ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <input
                        ref={entrada} type="file" accept=".csv,text/csv" aria-label="Escolher planilha CSV"
                        style={{ display: 'none' }}
                        onChange={(e) => { void receber(e.target.files?.[0]); e.target.value = ''; }}
                    />
                    {/* A área de arrastar é um botão: sem isso, quem usa teclado
                        não tem como chegar ao seletor de arquivo. */}
                    <button
                        type="button"
                        onDragOver={(e) => { e.preventDefault(); setSobreArea(true); }}
                        onDragLeave={() => setSobreArea(false)}
                        onDrop={(e) => { e.preventDefault(); setSobreArea(false); void receber(e.dataTransfer.files[0]); }}
                        onClick={() => entrada.current?.click()}
                        disabled={lendo}
                        style={{
                            width: '100%', border: `1.5px dashed ${sobreArea ? 'var(--burgundy-700)' : 'var(--border-strong)'}`,
                            borderRadius: 'var(--radius-md)', padding: 34, textAlign: 'center', cursor: lendo ? 'progress' : 'pointer',
                            background: sobreArea ? 'var(--surface-brand-soft)' : 'var(--surface-secondary)',
                            transition: 'var(--transition-control)',
                        }}
                    >
                        <Icon name="file-spreadsheet" size={22} style={{ color: sobreArea ? 'var(--text-brand)' : 'var(--neutral-500)' }} />
                        <p style={{ margin: '9px 0 0', fontFamily: 'var(--font-ui)', fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)' }}>
                            {lendo ? 'Lendo a planilha…' : 'Arraste a planilha aqui, ou clique para escolher'}
                        </p>
                        <p style={{ margin: '3px 0 0', fontFamily: 'var(--font-ui)', fontSize: 12.5, color: 'var(--text-secondary)' }}>
                            CSV até 10 MB · a primeira linha deve ser o cabeçalho · no Excel, Salvar como › CSV
                        </p>
                    </button>

                    <Alert tone="neutral" title="Não precisa arrumar o cabeçalho antes" dismissible={false}>
                        A planilha pode vir com "Celular", "Fone 1" ou "Nome do Paciente". No próximo passo você liga cada coluna ao campo certo.
                    </Alert>

                    <Button variant="link" onClick={baixarModelo} style={{ alignSelf: 'flex-start' }}>
                        Baixar planilha modelo
                    </Button>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {importando && <Alert tone="info" dismissible={false}>Gravando {arquivo.planilha.linhas.length} linhas…</Alert>}
                    <ImportMapper
                        arquivo={arquivo.nome} colunas={arquivo.planilha.colunas}
                        amostra={arquivo.planilha.linhas.slice(0, 5)} campos={CAMPOS[tipo]}
                        onCancelar={() => setArquivo(null)}
                        onConfirmar={(mapa) => { if (!importando) void importar(mapa); }}
                    />
                </div>
            )}
        </Modal>
    );
}
